/**
 * The priced Bill of Materials (APP_SPEC box 4): every quantity is
 * measured, every dollar carries provenance, and UNPRICED rows are
 * shown prominently -- the engine never fabricates a price, and the
 * honesty is the feature. The app-wide filter narrows the visible rows
 * on the axes line items carry (category, ifc_class).
 */
import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import type { ElementFilter } from '../../../api';
import { filterIsActive } from '../../../api';
import {
  AbsenceBadge,
  ActionButton,
  Figure,
  Grid,
  Notice,
  ProvenancePill,
  Stack,
  Text,
  type GridColumn,
} from '../../../strata';
import { useRefreshTakeoff, useTakeoff, type TakeoffLineItem } from '../queries';

/** UNPRICED is the ABSENCE of any rate (amber, never violation-red);
 * a priced line wears its provenance tier as the provenance pill. */
function provenanceTag(item: TakeoffLineItem) {
  // The pricing is a discriminated union in the contract: priced carries its rate's provenance,
  // unpriced carries nothing, which is the absence the badge wears (never a zero).
  if (item.pricing.kind === 'unpriced')
    return <AbsenceBadge label="UNPRICED" reason="no verified rate matched this line" />;
  return <ProvenancePill provenance={item.pricing.provenance.rate} />;
}

const money = (v: number) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COLUMNS: readonly GridColumn<TakeoffLineItem>[] = [
  {
    id: 'item',
    header: 'Item',
    cell: ({ row }) => (
      <Stack direction="column" gap="xs">
        <Text size="control">{row.original.sku}</Text>
        <Text size="micro" tone="secondary">
          {row.original.ifc_class}
          {row.original.category ? ` · ${row.original.category}` : ''}
        </Text>
      </Stack>
    ),
  },
  {
    id: 'qty',
    header: 'Qty',
    width: 140,
    numeric: true,
    cell: ({ row }) => (
      <Stack direction="column" gap="xs" align="flex-end">
        <Text size="control">
          {row.original.quantity.toLocaleString(undefined, { maximumFractionDigits: 1 })} {row.original.unit}
        </Text>
        {row.original.kind === 'linear' && (
          <Text size="micro" tone="secondary">
            {row.original.sticks} sticks
          </Text>
        )}
      </Stack>
    ),
  },
  {
    id: 'unit',
    header: 'Unit price',
    width: 110,
    numeric: true,
    cell: ({ row }) => (row.original.pricing.kind === 'priced' ? money(row.original.pricing.unit_price) : '--'),
  },
  {
    id: 'total',
    header: 'Total',
    width: 110,
    numeric: true,
    cell: ({ row }) => (row.original.pricing.kind === 'priced' ? money(row.original.pricing.total_cost) : '--'),
  },
  {
    id: 'provenance',
    header: 'Provenance',
    width: 150,
    cell: ({ row }) => provenanceTag(row.original),
  },
];

export function TakeoffPanel({ projectId }: { projectId: string }) {
  const filter = useSearch({ strict: false }) as ElementFilter;
  const { data, isLoading, isError } = useTakeoff(projectId, filter.scope);
  const refresh = useRefreshTakeoff(projectId, filter.scope);
  const filtered = filterIsActive(filter);

  const result = data && !data.queued ? data.result : null;
  const rows = useMemo(() => {
    if (!result) return [];
    return result.line_items.filter((item) => {
      if (filter.category?.length && !filter.category.includes(item.category ?? 'OTHER')) return false;
      if (filter.ifc_class && item.ifc_class !== filter.ifc_class) return false;
      return true;
    });
  }, [result, filter]);

  const rowKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return new Map(
      rows.map((item) => {
        const base = `${item.ifc_class}:${item.sku}`;
        const occurrence = seen.get(base) ?? 0;
        seen.set(base, occurrence + 1);
        return [item, `${base}:${occurrence}`] as const;
      }),
    );
  }, [rows]);

  if (isError) return <Notice kind="error" title="Takeoff failed to load." />;
  if (isLoading || !data) return <Text tone="secondary">Loading takeoff…</Text>;
  if (data.queued)
    return <Text tone="secondary">Takeoff is materializing on the worker -- this page updates itself.</Text>;

  const shownCost = rows.reduce((sum, r) => sum + (r.pricing.kind === 'priced' ? r.pricing.total_cost : 0), 0);
  const shownUnpriced = rows.filter((r) => r.pricing.kind === 'unpriced').length;

  return (
    <Stack direction="column" gap="md">
      <Stack direction="row" gap="xl" align="baseline" wrap>
        <Figure value={money(shownCost)} />
        <Text tone="secondary">
          {filtered ? `${rows.length} filtered lines` : `${rows.length} lines · ${result!.element_count} elements`}
          {shownUnpriced > 0 && (
            <>
              {' · '}
              <AbsenceBadge label={`${shownUnpriced} unpriced`} reason="no verified rate matched these lines" />
            </>
          )}
          {result!.rung !== 'IfcProject' && ` · priced over ${result!.rung.replace('Ifc', '')} ${result!.scope} alone`}
          {!filtered &&
            result!.total_sticks > 0 &&
            ` · ${result!.total_sticks} sticks (${result!.total_waste_inches.toFixed(0)}" waste)`}
        </Text>
        <ActionButton onPress={() => refresh.mutate()} isPending={refresh.isPending}>
          Refresh prices
        </ActionButton>
      </Stack>
      <Grid
        ariaLabel="Takeoff line items"
        rows={rows}
        columns={COLUMNS}
        rowKey={(item) => rowKeys.get(item) ?? `${item.ifc_class}:${item.sku}`}
        empty="No lines match the filter."
      />
    </Stack>
  );
}
